import React, { useState, useEffect, useCallback } from "react";
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  Modal,
  Form,
  Select,
  Avatar,
  Tooltip,
  Badge,
} from "antd";
import {
  FaUsers,
  FaSearch,
  FaEdit,
  FaUserTie,
  FaUserShield,
  FaUserCog,
  FaFilter,
  FaBuilding,
  FaIdBadge,
  FaEnvelope,
  FaLock,
  FaBriefcase,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Api from "../api/Api";

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────────── */
const PERFIS = [
  "JURIDICO",
  "COMERCIAL",
  "DIRETOR",
  "ENGENHARIA",
  "CORDENADOR",
  "OPERADOR",
    "ADMINISTRACAO",
];

const getPerfilInfo = (perfil) => {
  const map = {
    ADMIN: { color: "purple", icon: <FaUserShield /> },
    DIRETOR: { color: "gold", icon: <FaUserTie /> },
    ENGENHARIA: { color: "blue", icon: <FaUserCog /> },
    COMERCIAL: { color: "green", icon: <FaUserTie /> },
    JURIDICO: { color: "cyan", icon: <FaUserShield /> },
    CORDENADOR: { color: "orange", icon: <FaUserCog /> },
    OPERADOR: { color: "default", icon: <FaUsers /> },
  };
  return map[perfil] || { color: "default", icon: <FaUsers /> };
};

const getInitials = (nome) => {
  if (!nome) return "?";
  const parts = nome.trim().split(" ");
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const avatarColors = [
  "#1677ff", "#52c41a", "#faad14", "#ff4d4f",
  "#722ed1", "#13c2c2", "#eb2f96", "#fa8c16",
];
const getAvatarColor = (id) => avatarColors[id % avatarColors.length];

/* ─────────────────────────────────────────────────────────────────────────────
   MODAL DE EDITAR USUÁRIO
───────────────────────────────────────────────────────────────────────────── */
const EditarUsuarioModal = ({ open, onClose, onSuccess, usuario, direcoes }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && usuario) {
      form.setFieldsValue({
        nome: usuario.nome,
        email: usuario.email,
        funcao: usuario.funcao,
        direcao: usuario.direcao,
        perfil: usuario.perfil,
        password: "",
      });
    }
  }, [open, usuario, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        id: usuario.id,
        nome: values.nome || undefined,
        email: values.email || undefined,
        funcao: values.funcao || undefined,
        direcao: values.direcao || undefined,
        perfil: values.perfil || undefined,
      };

      if (values.password && values.password.trim() !== "") {
        payload.password = values.password;
      }

      await Api.AlterUserDados(payload);
      toast.success("Usuário atualizado com sucesso!");
      form.resetFields();
      onSuccess();
      onClose();
    } catch (error) {
      if (error.errorFields) {
        toast.error("Preencha todos os campos obrigatórios.");
      } else {
        toast.error("Erro ao atualizar usuário.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={640}
      title={
        <Space>
          <div
            style={{
              backgroundColor: "#e6f4ff",
              borderRadius: 10,
              padding: "8px 10px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <FaUserCog style={{ color: "#1677ff", fontSize: 18 }} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: "#1f1f1f" }}>
              Editar Usuário
            </div>
            {usuario && (
              <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                {usuario.employee_id} · {usuario.nome}
              </Text>
            )}
          </div>
        </Space>
      }
      footer={[
        <Button key="cancel" onClick={onClose} style={{ borderRadius: 8 }}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
          style={{ borderRadius: 8, fontWeight: 600 }}
        >
          Guardar Alterações
        </Button>,
      ]}
      styles={{ body: { padding: "24px 24px 0" } }}
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={
                <Space size={6}>
                  <FaUsers style={{ color: "#1677ff" }} />
                  <span style={{ fontWeight: 500 }}>Nome Completo</span>
                </Space>
              }
              name="nome"
              rules={[{ required: false }]}
            >
              <Input
                size="large"
                placeholder="Nome do usuário"
                style={{ borderRadius: 8 }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={
                <Space size={6}>
                  <FaEnvelope style={{ color: "#1677ff" }} />
                  <span style={{ fontWeight: 500 }}>E-mail</span>
                </Space>
              }
              name="email"
              rules={[{ type: "email", message: "E-mail inválido" }]}
            >
              <Input
                size="large"
                placeholder="email@technext.ao"
                style={{ borderRadius: 8 }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={
                <Space size={6}>
                  <FaLock style={{ color: "#1677ff" }} />
                  <span style={{ fontWeight: 500 }}>Nova Senha</span>
                </Space>
              }
              name="password"
              rules={[
                { min: 6, message: "Senha deve ter pelo menos 6 caracteres" },
              ]}
            >
              <Input.Password
                size="large"
                placeholder="Deixe em branco para não alterar"
                style={{ borderRadius: 8 }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={
                <Space size={6}>
                  <FaBriefcase style={{ color: "#1677ff" }} />
                  <span style={{ fontWeight: 500 }}>Função</span>
                </Space>
              }
              name="funcao"
            >
              <Input
                size="large"
                placeholder="Ex: Técnico de TI"
                style={{ borderRadius: 8 }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={
                <Space size={6}>
                  <FaBuilding style={{ color: "#1677ff" }} />
                  <span style={{ fontWeight: 500 }}>Direcção</span>
                </Space>
              }
              name="direcao"
            >
              <Select
                size="large"
                placeholder="Selecione a direcção"
                style={{ borderRadius: 8 }}
                showSearch
                optionFilterProp="children"
              >
                {direcoes.map((d) => (
                  <Option key={d.id} value={d.id}>
                    {d.descricao}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={
                <Space size={6}>
                  <FaUserShield style={{ color: "#1677ff" }} />
                  <span style={{ fontWeight: 500 }}>Perfil</span>
                </Space>
              }
              name="perfil"
            >
              <Select
                size="large"
                placeholder="Selecione o perfil"
                style={{ borderRadius: 8 }}
              >
                {PERFIS.map((p) => {
                  const info = getPerfilInfo(p);
                  return (
                    <Option key={p} value={p}>
                      <Space>
                        <Tag color={info.color} style={{ margin: 0 }}>
                          {p}
                        </Tag>
                      </Space>
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   PÁGINA PRINCIPAL
───────────────────────────────────────────────────────────────────────────── */
const EditarUsuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [direcoes, setDirecoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [perfilFilter, setPerfilFilter] = useState("TODOS");
  const [direcaoFilter, setDirecaoFilter] = useState("TODAS");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    document.title = "Gestão de Usuários | Dashboard";
  }, []);

  const loadDirecoes = useCallback(async () => {
    try {
      const response = await Api.ListDirecao();
      setDirecoes(Array.isArray(response) ? response : []);
    } catch {
      toast.error("Erro ao carregar direcções.");
      setDirecoes([]);
    }
  }, []);

  const loadUsuarios = useCallback(async () => {
    try {
      setLoading(true);
      const response = await Api.ListUser();
      setUsuarios(Array.isArray(response) ? response : []);
    } catch {
      toast.error("Erro ao carregar usuários.");
      setUsuarios([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDirecoes();
    loadUsuarios();
  }, [loadDirecoes, loadUsuarios]);

  const openModal = (user) => {
    setEditingUser(user);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingUser(null);
  };

  const getDirecaoNome = (id) => {
    const d = direcoes.find((d) => d.id === id);
    return d ? d.descricao : "—";
  };

  // Stats
  const totalAtivos = usuarios.filter((u) => u.status === "1").length;
  const totalInativos = usuarios.filter((u) => u.status === "0").length;
  const totalAdmin = usuarios.filter((u) => u.perfil === "ADMIN").length;

  // Filtered list
  const filtered = usuarios.filter((u) => {
    const matchPerfil = perfilFilter === "TODOS" || u.perfil === perfilFilter;
    const matchDirecao =
      direcaoFilter === "TODAS" || String(u.direcao) === String(direcaoFilter);
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      u.nome?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.employee_id?.toLowerCase().includes(q) ||
      u.funcao?.toLowerCase().includes(q);
    return matchPerfil && matchDirecao && matchSearch;
  });

  const columns = [
    {
      title: "Utilizador",
      key: "utilizador",
      width: 280,
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar
            size={42}
            style={{
              backgroundColor: getAvatarColor(record.id),
              fontWeight: 700,
              fontSize: 15,
              flexShrink: 0,
            }}
          >
            {getInitials(record.nome)}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600, color: "#1f1f1f", lineHeight: 1.3 }}>
              {record.nome}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <FaIdBadge style={{ marginRight: 4 }} />
              {record.employee_id}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "E-mail",
      dataIndex: "email",
      key: "email",
      width: 220,
      render: (email) => (
        <Text style={{ fontSize: 13 }}>
          <FaEnvelope style={{ marginRight: 6, color: "#8c8c8c" }} />
          {email}
        </Text>
      ),
    },
    {
      title: "Função",
      dataIndex: "funcao",
      key: "funcao",
      width: 180,
      render: (funcao) => (
        <Text style={{ fontSize: 13, textTransform: "capitalize" }}>
          {funcao || "—"}
        </Text>
      ),
    },
    {
      title: "Direcção",
      dataIndex: "direcao",
      key: "direcao",
      width: 200,
      render: (id) => (
        <Tooltip title={getDirecaoNome(id)}>
          <Text ellipsis style={{ maxWidth: 180, fontSize: 12 }}>
            <FaBuilding style={{ marginRight: 6, color: "#8c8c8c" }} />
            {getDirecaoNome(id)}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: "Perfil",
      dataIndex: "perfil",
      key: "perfil",
      width: 130,
      render: (perfil) => {
        const info = getPerfilInfo(perfil);
        return (
          <Tag
            color={info.color}
            icon={info.icon}
            style={{
              borderRadius: 20,
              padding: "4px 10px",
              fontWeight: 600,
              fontSize: 11,
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
            }}
          >
            {perfil}
          </Tag>
        );
      },
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) =>
        status === "1" ? (
          <Badge
            status="success"
            text={
              <Text style={{ fontSize: 13, color: "#52c41a", fontWeight: 500 }}>
                Ativo
              </Text>
            }
          />
        ) : (
          <Badge
            status="error"
            text={
              <Text style={{ fontSize: 13, color: "#ff4d4f", fontWeight: 500 }}>
                Inativo
              </Text>
            }
          />
        ),
    },
    {
      title: "Ações",
      key: "actions",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Tooltip title="Editar usuário">
          <Button
            type="primary"
            ghost
            icon={<FaEdit />}
            onClick={() => openModal(record)}
            style={{
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
            size="small"
          >
            Editar
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <Layout style={{ background: "transparent" }}>
      <Content style={{ padding: "24px 32px" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        {/* Cabeçalho */}
        <div
          style={{
            background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
            borderRadius: 16,
            padding: "24px 32px",
            marginBottom: 32,
            boxShadow: "0 8px 20px rgba(22, 119, 255, 0.15)",
          }}
        >
          <Row align="middle" justify="space-between">
            <Col>
              <Space size="middle">
                <div
                  style={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderRadius: 12,
                    padding: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaUsers style={{ fontSize: 32, color: "#fff" }} />
                </div>
                <div>
                  <Title
                    level={2}
                    style={{ color: "#fff", margin: 0, fontWeight: 700 }}
                  >
                    Gestão de Utilizadores
                  </Title>
                  <Text
                    style={{ color: "rgba(255,255,255,0.85)", fontSize: 16 }}
                  >
                    Gerencie perfis, acessos e dados dos utilizadores
                  </Text>
                </div>
              </Space>
            </Col>
          </Row>
        </div>

        {/* Cards de estatísticas */}
        <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
          <Col xs={24} sm={8}>
            <Card
              bordered={false}
              style={{
                borderRadius: 16,
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                border: "1px solid #f0f0f0",
              }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Statistic
                title={
                  <span style={{ fontSize: 14, color: "#8c8c8c" }}>
                    Total de Utilizadores
                  </span>
                }
                value={usuarios.length}
                valueStyle={{ fontSize: 32, fontWeight: 700, color: "#1677ff" }}
                prefix={
                  <div
                    style={{
                      backgroundColor: "#e6f4ff",
                      borderRadius: 12,
                      padding: 10,
                      marginRight: 16,
                    }}
                  >
                    <FaUsers style={{ fontSize: 22, color: "#1677ff" }} />
                  </div>
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card
              bordered={false}
              style={{
                borderRadius: 16,
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                border: "1px solid #f0f0f0",
              }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Statistic
                title={
                  <span style={{ fontSize: 14, color: "#8c8c8c" }}>
                    Utilizadores Ativos
                  </span>
                }
                value={totalAtivos}
                valueStyle={{ fontSize: 32, fontWeight: 700, color: "#52c41a" }}
                prefix={
                  <div
                    style={{
                      backgroundColor: "#f6ffed",
                      borderRadius: 12,
                      padding: 10,
                      marginRight: 16,
                    }}
                  >
                    <FaCheckCircle style={{ fontSize: 22, color: "#52c41a" }} />
                  </div>
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card
              bordered={false}
              style={{
                borderRadius: 16,
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                border: "1px solid #f0f0f0",
              }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Statistic
                title={
                  <span style={{ fontSize: 14, color: "#8c8c8c" }}>
                    Administradores
                  </span>
                }
                value={totalAdmin}
                valueStyle={{ fontSize: 32, fontWeight: 700, color: "#722ed1" }}
                prefix={
                  <div
                    style={{
                      backgroundColor: "#f9f0ff",
                      borderRadius: 12,
                      padding: 10,
                      marginRight: 16,
                    }}
                  >
                    <FaUserShield style={{ fontSize: 22, color: "#722ed1" }} />
                  </div>
                }
              />
            </Card>
          </Col>
        </Row>

        {/* Barra de ferramentas */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            marginBottom: 24,
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
          bodyStyle={{ padding: "16px 24px" }}
        >
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Input
                size="large"
                prefix={<FaSearch style={{ color: "#bfbfbf" }} />}
                placeholder="Pesquisar por nome, e-mail, ID ou função..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
                style={{ borderRadius: 30 }}
              />
            </Col>
            <Col>
              <Select
                size="large"
                style={{ width: 170 }}
                value={perfilFilter}
                onChange={setPerfilFilter}
                suffixIcon={<FaFilter style={{ color: "#1677ff" }} />}
              >
                <Option value="TODOS">Todos os perfis</Option>
                {PERFIS.map((p) => (
                  <Option key={p} value={p}>
                    {p}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col>
              <Select
                size="large"
                style={{ width: 200 }}
                value={direcaoFilter}
                onChange={setDirecaoFilter}
                suffixIcon={<FaBuilding style={{ color: "#1677ff" }} />}
              >
                <Option value="TODAS">Todas as direcções</Option>
                {direcoes.map((d) => (
                  <Option key={d.id} value={String(d.id)}>
                    {d.descricao}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>
        </Card>

        {/* Tabela */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
          bodyStyle={{ padding: 0 }}
        >
          <Table
            columns={columns}
            dataSource={filtered}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total de ${total} utilizadores`,
              style: { padding: "16px 24px" },
            }}
            locale={{ emptyText: "Nenhum utilizador encontrado" }}
            scroll={{ x: 1100 }}
          />
        </Card>

        {/* Modal de edição */}
        <EditarUsuarioModal
          open={modalOpen}
          onClose={closeModal}
          onSuccess={loadUsuarios}
          usuario={editingUser}
          direcoes={direcoes}
        />
      </Content>
    </Layout>
  );
};

export default EditarUsuarios;